import { useEffect, useRef, useState } from "react";
import { useGsap } from "@/hooks/useGsap";
import { gsap, ScrollTrigger, prefersReducedMotion, canAnimateHeavy } from "@/lib/motion";
import { WRAP } from "@/components/story/layout";
import {
  BriefArtifact,
  SystemArtifact,
  InterfaceArtifact,
  FlowArtifact,
  MetricArtifact,
} from "@/components/story/Artifacts";

/**
 * Moment 3: the five stages of the work, made visible.
 *
 * Desktop: the block sticks for a short distance while a cobalt rail fills and
 * each stage lights in turn. The distance is 180svh rather than the 250svh the
 * brief asked for, because this repo already removed one set of pinned scenes
 * for producing dead scroll (CMD_01 in LEARNINGS.md). 180svh is roughly one
 * unhurried flick per stage and no more.
 *
 * Mobile and reduced motion: an ordinary vertical timeline. Nothing is pinned,
 * every stage is expanded, every artifact is drawn. No information lives only
 * inside the animation.
 */

const STAGES = [
  ["Understand", "We find the real problem behind the request.", BriefArtifact],
  ["Design", "We turn a vague ask into a system you can point at.", SystemArtifact],
  ["Build", "We build the tool, and it works on the factory floor.", InterfaceArtifact],
  ["Automate", "We take the repeat work out of somebody's day.", FlowArtifact],
  ["Improve", "We measure it, fix what drifts, and keep it useful.", MetricArtifact],
];

/* Decided before the first paint, not in an effect: the pinned layout changes
   the height of the scroll track, and ScrollTrigger has to measure the height
   it will actually have. */
const heavyLayout = () => canAnimateHeavy() && !prefersReducedMotion();

/* Kept in one place: the sticky offset, the container height and the fit test
   all have to agree with the header, or the block clips. */
const HEADER_H = 72;

export default function MethodScene() {
  const [pinned, setPinned] = useState(heavyLayout);
  const [active, setActive] = useState(() => (heavyLayout() ? 0 : STAGES.length - 1));
  const gridRef = useRef(null);

  /* A pinned block is a fixed-height box with overflow hidden, so content that
     does not fit is content the reader never sees. Raising the type scale once
     pushed this 106px over at 1024px wide and quietly clipped the heading.
     Rather than tune numbers that the next type change will break again, the
     layout measures itself: if the content does not fit the viewport it gives
     up pinning and becomes an ordinary vertical section, which always works.
     Runs after paint, and again on resize. */
  useEffect(() => {
    const fits = () => {
      const grid = gridRef.current;
      if (!grid) return true;
      /* 24px of slack so a block that only just fits is not pinned to the
         millimetre. */
      return grid.scrollHeight + 24 <= window.innerHeight - HEADER_H;
    };

    const decide = () => {
      const wanted = heavyLayout();
      const next = wanted && fits();
      setPinned((prev) => {
        if (prev === next) return prev;
        if (!next) setActive(STAGES.length - 1);
        return next;
      });
    };

    decide();
    window.addEventListener("resize", decide);
    return () => window.removeEventListener("resize", decide);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinned]);

  const scope = useGsap((ctx, el) => {
    if (!pinned) {
      setActive(STAGES.length - 1);
      return;
    }

    const track = el.querySelector("[data-track]");
    const fill = el.querySelector("[data-fill]");
    if (!track || !fill) return;

    try {
      ScrollTrigger.create({
        trigger: track,
        start: "top top",
        end: "bottom bottom",
        /* Scrub rather than snap: the rail should follow the hand. */
        scrub: 0.8,
        onUpdate: (self) => {
          gsap.set(fill, { scaleY: self.progress });
          /* Five equal bands. The last band holds to the end so the final
             stage is readable rather than flashing past. */
          const i = Math.min(STAGES.length - 1, Math.floor(self.progress * STAGES.length));
          setActive(i);
        },
      });
    } catch {
      setPinned(false);
      setActive(STAGES.length - 1);
    }
  }, [pinned]);

  const ActiveArtifact = STAGES[active][2];

  return (
    <section ref={scope} id="method" className="border-b border-ink scroll-mt-20" data-testid="section-method">
      <div data-track style={pinned ? { height: "180svh" } : undefined}>
        <div className={pinned ? "method-pinned sticky top-[72px] h-[calc(100svh-72px)] flex items-center overflow-hidden" : ""}>
          <div className={`${WRAP} w-full ${pinned ? "py-0" : "story-pad"}`}>
            <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-6 items-start lg:items-center">
              {/* Heading and stage list */}
              <div className="lg:col-span-6">
                <p className="story-index">02 / The method</p>
                <h2 className="display story-h2 mt-5 max-w-[16ch]">We make the path visible.</h2>

                <div className="mt-8 flex gap-6">
                  {/* The rail. On the non-pinned path it is a plain hairline
                      and never fills, which is correct: nothing is scrubbing. */}
                  <div className="method-rail method-rail--v shrink-0" aria-hidden="true">
                    <div data-fill className="method-rail__fill" />
                  </div>

                  <ol className="flex-1">
                    {STAGES.map(([name, line, Artifact], i) => (
                      <li
                        key={name}
                        className="method-stage py-3.5 md:py-4 border-b border-ink/15"
                        data-active={pinned ? i === active : true}
                        data-done={pinned ? i < active : false}
                        data-testid={`method-stage-${i}`}
                      >
                        <div className="flex items-start gap-4">
                          <span className="method-stage__marker mt-2 shrink-0" aria-hidden="true" />
                          <div className="min-w-0">
                            <h3 className="method-stage__name display text-[28px] md:text-[36px] leading-[1] tracking-[-0.02em]">{name}</h3>
                            <p className="method-stage__line mt-2 story-body text-ink/65 max-w-[42ch]">{line}</p>
                          </div>
                        </div>

                        {/* Mobile and reduced motion: each stage carries its own
                            artifact inline, because there is no pinned panel. */}
                        {!pinned ? (
                          <div className="mt-5 max-w-[320px] ml-[29px]">
                            <Artifact />
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              {/* The pinned artifact panel. Desktop only. */}
              {pinned ? (
                <div className="hidden lg:block lg:col-span-5 lg:col-start-8">
                  <div className="border border-ink p-8 bg-paper">
                    <p className="eyebrow">{`Stage ${String(active + 1).padStart(2, "0")} of 05`}</p>
                    <div className="mt-6">
                      <ActiveArtifact />
                    </div>
                    <p className="mt-6 story-body text-ink/70 max-w-[34ch]">{STAGES[active][1]}</p>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
